import { useMemo } from 'react';
import { Alert, Badge } from '@playerbase/ui';
import type { DiscordGuildChannel } from '@playerbase/types';

// ---------------------------------------------------------------------------
// Inline channel multi-select. Mirrors the body of `add-channel-modal.tsx`
// (grouped by parent, channel-type badges, Discord-style ordering) but
// without the modal chrome, backfill input, or "already watched"
// filtering. Hosts: the Recruit card on the form builder for now;
// future card-style pickers can reuse it.
//
// Selection is fully controlled by the parent so it round-trips through
// the same builder reducer that owns every other piece of metadata. No
// internal state.
// ---------------------------------------------------------------------------

interface DiscordChannelMultiSelectProps {
  channels: DiscordGuildChannel[];
  selectedIds: ReadonlyArray<string>;
  onChange: (next: string[]) => void;
  loading: boolean;
  disabled?: boolean;
  // Empty-state copy. Pickers in different surfaces want different
  // wording (e.g. "no channels visible" vs "the bot hasn't synced
  // yet"); leave it to the host.
  emptyMessage?: string;
}

interface Group {
  parentName: string;
  channels: DiscordGuildChannel[];
}

function groupByParent(channels: DiscordGuildChannel[]): Group[] {
  const groups = new Map<string, DiscordGuildChannel[]>();
  const topLevelKey = '__top__';
  for (const c of channels) {
    const key = c.parentName ?? topLevelKey;
    const list = groups.get(key);
    if (list === undefined) groups.set(key, [c]);
    else list.push(c);
  }
  const out: Group[] = [];
  if (groups.has(topLevelKey)) {
    out.push({ parentName: 'Top level', channels: groups.get(topLevelKey) ?? [] });
    groups.delete(topLevelKey);
  }
  for (const [name, list] of [...groups.entries()].sort(([a], [b]) => a.localeCompare(b))) {
    out.push({ parentName: name, channels: list });
  }
  return out;
}

function channelTypeBadge(type: DiscordGuildChannel['type']): string {
  switch (type) {
    case 'forum':
      return 'Forum';
    case 'announcement':
      return 'Announcements';
    case 'thread':
      return 'Thread';
    case 'text':
    default:
      return 'Text';
  }
}

export function DiscordChannelMultiSelect({
  channels,
  selectedIds,
  onChange,
  loading,
  disabled = false,
  emptyMessage = 'The bot has no readable channels in this server yet.',
}: DiscordChannelMultiSelectProps): JSX.Element {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  // Threads (forum posts and ad-hoc message-reply threads alike) are
  // never sensible picker options: the bot posts into channels, not into
  // individual conversations under them. Strip them out before grouping.
  const visibleChannels = useMemo(
    () => channels.filter((c) => c.type !== 'thread'),
    [channels],
  );

  const groups = useMemo(() => groupByParent(visibleChannels), [visibleChannels]);

  // Stale-id guard: a channel previously picked may have been deleted in
  // Discord and dropped from the mirror. Surface them as a separate
  // "missing" group so the studio knows their selection no longer
  // resolves and can clear it.
  const missingIds = useMemo(() => {
    const known = new Set(channels.map((c) => c.channelId));
    return selectedIds.filter((id) => !known.has(id));
  }, [channels, selectedIds]);

  function toggle(channelId: string): void {
    if (selectedSet.has(channelId)) {
      onChange(selectedIds.filter((id) => id !== channelId));
    } else {
      onChange([...selectedIds, channelId]);
    }
  }

  if (loading && channels.length === 0) {
    return <p className="text-sm text-text-muted">Loading channels…</p>;
  }
  if (channels.length === 0 && missingIds.length === 0) {
    return <Alert tone="info">{emptyMessage}</Alert>;
  }

  return (
    <div className="max-h-72 overflow-y-auto rounded-md border border-border">
      <ul className="divide-y divide-border">
        {groups.map((group) => (
          <li key={group.parentName} className="px-3 py-2">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-text-muted">
              {group.parentName}
            </p>
            <ul className="space-y-1">
              {group.channels.map((c) => {
                const ticked = selectedSet.has(c.channelId);
                return (
                  <li key={c.channelId}>
                    <label
                      className={`flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm ${
                        disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-accent-bg'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={ticked}
                        disabled={disabled}
                        onChange={() => toggle(c.channelId)}
                      />
                      <span className="flex-1 truncate"># {c.name}</span>
                      <Badge tone="info">{channelTypeBadge(c.type)}</Badge>
                    </label>
                  </li>
                );
              })}
            </ul>
          </li>
        ))}
        {missingIds.length > 0 ? (
          <li className="px-3 py-2">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-danger">
              Missing
            </p>
            <ul className="space-y-1">
              {missingIds.map((id) => (
                <li key={id}>
                  <label
                    className={`flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm ${
                      disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-accent-bg'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked
                      disabled={disabled}
                      onChange={() => toggle(id)}
                    />
                    <span className="flex-1 truncate text-text-muted">
                      Channel no longer visible ({id})
                    </span>
                    <Badge tone="warning">Stale</Badge>
                  </label>
                </li>
              ))}
            </ul>
          </li>
        ) : null}
      </ul>
    </div>
  );
}
